import { faClipboardList } from '@fortawesome/free-solid-svg-icons/faClipboardList';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons/faEllipsis';
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye';
import { faMessage } from '@fortawesome/free-solid-svg-icons/faMessage';
import { faToggleOff } from '@fortawesome/free-solid-svg-icons/faToggleOff';
import { faToggleOn } from '@fortawesome/free-solid-svg-icons/faToggleOn';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { TFunction } from 'i18next';
import styled from 'styled-components';

import { Button } from '../../../components/Button';
import { CopyButton } from '../../../components/CopyButton';
import { Flex } from '../../../components/Flex';
import { Text } from '../../../components/Text';
import { TextWithTruncated } from '../../../components/TextWithTruncated';
import { SearchUsersResponseDto } from '../../../model/admin/dto/SearchUsersResponseDto';
import { FontSize } from '../../../theme/FontSize';
import { Spacing } from '../../../theme/Spacing';
import { Dropdown, MenuProps, TableProps } from '../../../utils/local-antd';

type UserFNType = ATVoidFunction<
  Partial<SearchUsersResponseDto['data'][number]>
>;

type FieldFunctions = {
  t: TFunction;
  toggleStatus: UserFNType;
  sendMessage: UserFNType;
  managePlan: UserFNType;
  view: ATVoidFunction<string>;
  delete: UserFNType;
};

const StyledCopyButton = styled(CopyButton)`
  padding: 0;
`;

const MenuButton = styled(Button)`
  margin: 0;
  background-color: ${({ theme }) => theme.transparent};
  color: ${({ theme }) => theme.main};
`;

const StyledActionButton = styled(Button)`
  background-color: transparent;
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  padding: 0;
  margin: 0;
  width: 100%;
  height: 100%;
  color: ${({ theme, color }) => theme[color || 'main']};
  font-size: ${FontSize.m};
`;

const getFieldWidth = (
  field:
    | keyof SearchUsersResponseDto['data'][number]
    | keyof SearchUsersResponseDto['data'][number]['user']
    | 'actions',
) => {
  switch (field) {
    case 'actions':
      return '5%';
    case 'name':
      return 140;
    case 'email':
      return 175;
    case 'plan':
    case 'createdAt':
    case 'atiraLinkCount':
    case 'shortURLsCount':
    case 'qrsCount':
      return 100;

    default:
      return 100;
  }
};

const getFieldInfo = (
  column:
    | keyof SearchUsersResponseDto['data'][number]
    | keyof SearchUsersResponseDto['data'][number]['user']
    | 'actions',
  functions: FieldFunctions,
) => {
  const { t } = functions;

  switch (column) {
    case 'actions':
      return {
        title: t('common.actions'),
        key: 'table-actions',
        data: column,
        render: (data: SearchUsersResponseDto['data'][number]) => {
          const isEnabled = data.user.enabled === true;
          const items: MenuProps['items'] = [
            {
              key: 'view',
              children:
                data.atiraLinkCount > 1
                  ? data.user.atiraLinks?.map((link) => ({
                      key: link?._id,
                      label: (
                        <StyledActionButton
                          icon={faEye}
                          onClick={() => functions.view(link?.linkName)}
                        >
                          {link?.linkName}
                        </StyledActionButton>
                      ),
                    }))
                  : undefined,
              label: (
                <StyledActionButton
                  icon={faEye}
                  disabled={!data?.user?.atiraLinks?.[0]?.linkName!}
                  onClick={() =>
                    data.atiraLinkCount > 1
                      ? null
                      : functions.view(data.user?.atiraLinks?.[0]?.linkName!)
                  }
                >
                  {t('common.preview')}
                </StyledActionButton>
              ),
            },
            {
              key: 'enable-disable',
              label: (
                <StyledActionButton
                  icon={isEnabled ? faToggleOff : faToggleOn}
                  onClick={() => functions.toggleStatus(data)}
                >
                  {t(isEnabled ? 'common.disable' : 'common.enable')}
                </StyledActionButton>
              ),
            },
            {
              key: 'send-message',
              label: (
                <StyledActionButton
                  icon={faMessage}
                  onClick={() => functions.sendMessage(data)}
                >
                  {t('common.message')}
                </StyledActionButton>
              ),
            },
            {
              key: 'manage-plan',
              label: (
                <StyledActionButton
                  icon={faClipboardList}
                  onClick={() => functions.managePlan(data)}
                >
                  {t('admin.managePlan')}
                </StyledActionButton>
              ),
            },
            {
              key: 'delete-user',
              label: (
                <StyledActionButton
                  icon={faTrash}
                  onClick={() => functions.delete(data)}
                  color="danger"
                >
                  {t('common.delete')}
                </StyledActionButton>
              ),
            },
          ];
          return (
            <Dropdown menu={{ items }} trigger={['click']}>
              <MenuButton icon={faEllipsis} />
            </Dropdown>
          );
        },

        fixed: 'left',
        width: getFieldWidth(column),
      };

    case 'name':
      return {
        title: t('common.name'),
        key: 'table-name',
        data: column,
        render: (
          text: string,
          record: SearchUsersResponseDto['data'][number],
        ) => {
          const name = record.user?.name;
          return name ? (
            <TextWithTruncated length={20}>{name}</TextWithTruncated>
          ) : (
            ''
          );
        },
        sorter: (
          a: SearchUsersResponseDto['data'][number],
          b: SearchUsersResponseDto['data'][number],
        ) => a.user?.name?.localeCompare(b.user?.name),
        width: getFieldWidth(column),
      };

    case 'email':
      return {
        title: t('common.email'),
        key: 'table-email',
        data: column,
        render: (
          text: string,
          record: SearchUsersResponseDto['data'][number],
        ) => {
          const email = record.user?.email;
          return email ? (
            <Flex alignItems="center" justifyContent="space-between" gap="m">
              <TextWithTruncated length={25}>{email}</TextWithTruncated>
              <StyledCopyButton copy={email} />
            </Flex>
          ) : (
            ''
          );
        },
        width: getFieldWidth(column),
      };

    case 'plan':
      return {
        title: t('common.plan'),
        key: 'table-plan',
        data: column,
        render: (
          text: string,
          record: SearchUsersResponseDto['data'][number],
        ) => {
          return <Text>{record.plan?.type}</Text>;
        },
        width: getFieldWidth(column),
      };

    case 'atiraLinkCount':
      return {
        title: t('common.atira_links'),
        key: 'table-atira-link-count',
        render: (user: SearchUsersResponseDto['data'][number]) => {
          return <Text>{user.atiraLinkCount}</Text>;
        },

        sortable: true,
        reorder: true,
        data: column,
        width: getFieldWidth(column),
      };

    case 'qrsCount':
      return {
        title: t('common.qrs'),
        key: 'table-qrs-count',
        render: (user: SearchUsersResponseDto['data'][number]) => {
          return <Text>{user.shortURLsCount}</Text>;
        },

        sortable: true,
        reorder: true,
        data: column,
        width: getFieldWidth(column),
      };

    case 'shortURLsCount':
      return {
        title: t('common.short_urls'),
        key: 'table-short-urls-count',
        render: (user: SearchUsersResponseDto['data'][number]) => {
          return <Text>{user.shortURLsCount}</Text>;
        },

        sortable: true,
        reorder: true,
        data: column,
        width: getFieldWidth(column),
      };

    case 'createdAt':
      return {
        title: t('common.created_at'),
        key: 'table-createdAt',
        data: column,
        render: (
          text: string,
          record: SearchUsersResponseDto['data'][number],
        ) => {
          const createdAt = record.user.createdAt;
          return createdAt ? new Date(createdAt).toLocaleDateString() : '';
        },
        sorter: (
          a: SearchUsersResponseDto['data'][number],
          b: SearchUsersResponseDto['data'][number],
        ) =>
          new Date(a.user.createdAt).getTime() -
          new Date(b.user.createdAt).getTime(),
        width: getFieldWidth('createdAt'),
      };
  }
};

export function createAdminSearchUsersTableColumns<T>(
  functions: FieldFunctions,
) {
  const tableColumnsKeys: (
    | keyof SearchUsersResponseDto['data'][number]
    | keyof SearchUsersResponseDto['data'][number]['user']
    | 'actions'
  )[] = [
    'actions',
    'name',
    'email',
    'plan',
    'createdAt',
    'atiraLinkCount',
    'shortURLsCount',
    'qrsCount',
  ];

  return tableColumnsKeys.map((c) =>
    getFieldInfo(c, functions),
  ) as TableProps<T>['columns'];
}
